import React, { useState, useRef, useEffect, useMemo } from 'react';
import './MultiSelectDropdown.css';

function useOnClickOutside(ref, handler) {
  useEffect(() => {
    const listener = (e) => {
      if (!ref.current || ref.current.contains(e.target)) return;
      handler(e);
    };
    document.addEventListener('mousedown', listener);
    document.addEventListener('touchstart', listener);
    return () => {
      document.removeEventListener('mousedown', listener);
      document.removeEventListener('touchstart', listener);
    };
  }, [ref, handler]);
}

/**
 * Multi-select dropdown with trigger (placeholder/summary) and dropdown panel
 * containing "Select All" + list of checkboxes. Matches Site-style dropdown UI.
 * @param {string} placeholder - e.g. "Select CRA"
 * @param {Array<{value: string, label: string}>} options
 * @param {string[]} selectedValues - selected option values
 * @param {function(string[]): void} onChange
 * @param {boolean} [disabled]
 * @param {function(object): string} [renderLabel] - optional (opt) => string for option label
 * @param {boolean} [inlineSummary] - if true, selected labels in one line (comma-separated); if false, one label per row
 * @param {boolean} [searchable] - show search input in panel; Select All applies to currently filtered options
 * @param {string} [searchPlaceholder]
 */
function MultiSelectDropdown({
  placeholder,
  options,
  selectedValues,
  onChange,
  disabled,
  renderLabel,
  inlineSummary = false,
  searchable = false,
  searchPlaceholder = 'Search…',
}) {
  const [open, setOpen] = useState(false);
  const [filterText, setFilterText] = useState('');
  const ref = useRef(null);
  const selectAllRef = useRef(null);
  const searchInputRef = useRef(null);
  useOnClickOutside(ref, () => {
    setOpen(false);
    setFilterText('');
  });

  const norm = (v) => (v == null ? '' : String(v));
  const selectedSet = new Set((selectedValues || []).map(norm));

  const displayOptions = useMemo(() => {
    if (!searchable) return options;
    const q = filterText.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => {
      const lab = String(renderLabel ? renderLabel(o) : o.label || '').toLowerCase();
      return lab.includes(q) || norm(o.value).toLowerCase().includes(q);
    });
  }, [options, filterText, searchable, renderLabel]);

  const allValues = displayOptions.map((o) => norm(o.value));
  const allSelected = allValues.length > 0 && allValues.every((v) => selectedSet.has(v));
  const someInDisplaySelected =
    displayOptions.length > 0 && displayOptions.some((o) => selectedSet.has(norm(o.value)));

  useEffect(() => {
    const el = selectAllRef.current;
    if (!el) return;
    el.checked = allSelected;
    el.indeterminate = someInDisplaySelected && !allSelected;
  }, [allSelected, someInDisplaySelected, open, displayOptions.length]);

  useEffect(() => {
    if (open && searchable && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [open, searchable]);

  const selectedOptions = options.filter((o) => selectedSet.has(norm(o.value)));
  const selectedLabels = selectedOptions.map((o) =>
    renderLabel ? renderLabel(o) : o.label
  );
  const summaryTitle =
    selectedLabels.length === 0 ? placeholder : selectedLabels.join('\n');

  const handleSelectAll = () => {
    if (allValues.length === 0) return;
    const removeSet = new Set(allValues);
    if (allSelected) {
      onChange((selectedValues || []).map(norm).filter((v) => !removeSet.has(v)));
    } else {
      const next = new Set((selectedValues || []).map(norm));
      allValues.forEach((v) => next.add(v));
      onChange(Array.from(next));
    }
  };

  return (
    <div className="multiselect-dropdown" ref={ref}>
      <button
        type="button"
        className={`multiselect-dropdown-trigger${inlineSummary ? '' : ' multiselect-dropdown-trigger--stacked'}`}
        onClick={() => !disabled && setOpen((o) => !o)}
        disabled={disabled}
        title={summaryTitle}
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        <span
          className={
            inlineSummary
              ? 'multiselect-dropdown-summary'
              : 'multiselect-dropdown-summary multiselect-dropdown-summary--stacked'
          }
        >
          {selectedLabels.length === 0 ? (
            <span className="multiselect-dropdown-placeholder">{placeholder}</span>
          ) : inlineSummary ? (
            selectedLabels.join(', ')
          ) : (
            selectedLabels.map((text, idx) => (
              <span key={idx} className="multiselect-dropdown-summary-line">
                {text}
              </span>
            ))
          )}
        </span>
        <span className="multiselect-dropdown-arrow">▼</span>
      </button>
      {open && (
        <div className="multiselect-dropdown-panel" role="listbox">
          {searchable && (
            <div className="multiselect-dropdown-search-wrap">
              <input
                ref={searchInputRef}
                type="search"
                className="multiselect-dropdown-search"
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
                placeholder={searchPlaceholder}
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
                aria-label={searchPlaceholder}
              />
            </div>
          )}
          <label className="multiselect-dropdown-option multiselect-dropdown-select-all">
            <input
              type="checkbox"
              ref={selectAllRef}
              checked={allSelected}
              onChange={handleSelectAll}
            />
            <span>Select All</span>
          </label>
          {displayOptions.length === 0 && searchable && options.length > 0 ? (
            <div className="multiselect-dropdown-empty">No matches</div>
          ) : (
            displayOptions.map((opt) => (
              <label key={opt.value} className="multiselect-dropdown-option">
                <input
                  type="checkbox"
                  checked={selectedSet.has(norm(opt.value))}
                  onChange={(e) => {
                    const newSet = new Set((selectedValues || []).map(norm));
                    if (e.target.checked) newSet.add(norm(opt.value));
                    else newSet.delete(norm(opt.value));
                    onChange(Array.from(newSet));
                  }}
                />
                <span>{renderLabel ? renderLabel(opt) : opt.label}</span>
              </label>
            ))
          )}
        </div>
      )}
    </div>
  );
}

export default MultiSelectDropdown;
